/**
 * VISTOERA_SAAS_I18N_FOUNDATION V1 — canonical `LocaleProvider` (spec §14).
 *
 * THE ONE locale source for the entire SaaS app: Content Project, Canvas,
 * Platform Admin, marketing shell — every Surface reads the current
 * `UiLocale` through `useUiLocale()` from this same provider. No Surface
 * may keep its own separate locale `useState` (spec §14 explicitly forbids
 * "Content Project 自己一个 locale state / Canvas 自己一个 / Admin 自己一个").
 *
 * Mount ONCE, at the very root of the app (see `App.tsx`) — above the
 * `ConfigProvider`, since `ConfigProvider`'s own `locale` prop is derived
 * from this provider's state, not the other way around.
 *
 * Switching locale here is a pure UI-presentation change (spec §42): it
 * never touches route state, never triggers a network request, never
 * regenerates/mutates any AI task or business data. See
 * `resolveInitialUiLocale`/`persistUiLocale` (`./localeStorage.ts`) for
 * where the value comes from / is remembered, and `./antdLocale.ts` for
 * the AntD + dayjs binding this provider keeps in sync on every change.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import type { Locale as AntdLocale } from 'antd/es/locale'
import { I18nextProvider } from 'react-i18next'
import { ensureI18nInitialized } from './config'
import { getAntdLocale, syncDayjsLocale } from './antdLocale'
import { resolveInitialUiLocale, persistUiLocale } from './localeStorage'
import type { UiLocale } from './types'

const i18nInstance = ensureI18nInitialized()

interface UiLocaleContextValue {
  /** The current UI Locale — SaaS chrome only (spec §1.A). Never read this
   * to decide the language of user-generated content. */
  locale: UiLocale
  /** The AntD `ConfigProvider` locale object matching the current UI
   * Locale — kept here (rather than re-derived at each call site) so
   * `App.tsx` and this provider can never disagree (spec §12). */
  antdLocale: AntdLocale
  /** Switches the UI Locale everywhere at once, persists the choice, and
   * re-syncs AntD/dayjs. Never triggers a full page reload (spec §17
   * `FULL_PAGE_RELOAD_REQUIRED = NO`) and never touches business/route
   * state (spec §42). */
  setLocale: (next: UiLocale) => void
}

const UiLocaleContext = createContext<UiLocaleContextValue | null>(null)

// Applied once at module load too (not only inside the component's
// effect-less first render) so the very first paint — including anything
// rendered before React commits — already has the right dayjs default.
syncDayjsLocale(resolveInitialUiLocale().locale)

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<UiLocale>(() => resolveInitialUiLocale().locale)

  const setLocale = useCallback((next: UiLocale) => {
    setLocaleState((prev) => {
      if (prev === next) return prev
      void i18nInstance.changeLanguage(next)
      syncDayjsLocale(next)
      persistUiLocale(next)
      return next
    })
  }, [])

  const value = useMemo<UiLocaleContextValue>(
    () => ({ locale, antdLocale: getAntdLocale(locale), setLocale }),
    [locale, setLocale],
  )

  return (
    <I18nextProvider i18n={i18nInstance}>
      <UiLocaleContext.Provider value={value}>{children}</UiLocaleContext.Provider>
    </I18nextProvider>
  )
}

/** The one hook every Surface must use to read/change the current UI
 * Locale (spec §14). Throws loudly if used outside `<LocaleProvider>`
 * rather than silently defaulting, so a missing provider is caught in
 * dev, not shipped. */
export function useUiLocale(): UiLocaleContextValue {
  const ctx = useContext(UiLocaleContext)
  if (!ctx) {
    throw new Error('useUiLocale() must be used within <LocaleProvider>')
  }
  return ctx
}
